/* ============================================================
   AM2T — component-select.js
   Styled dropdowns for every <select> in the theme.

   Progressive enhancement: the native <select> stays in the DOM (it is
   what forms submit and what other scripts listen to), but it is
   visually hidden and a button + listbox in the AM2T style is shown in
   its place. Picking an option sets select.value and fires the normal
   'input' + 'change' events, so existing code (contact form, Competition
   Pack, …) keeps working untouched.

   Kept in sync automatically when other scripts:
   - rebuild the options (innerHTML / appendChild)
   - toggle hidden / disabled / class on the select
   - set select.value or select.selectedIndex in JS

   Opt out for a single select with data-native-select.
   ============================================================ */

(() => {
  'use strict';

  if (window.AM2TSelect) return;

  const SELECTOR = 'select:not([multiple]):not([data-native-select]):not([data-am2t-enhanced])';
  const proto = HTMLSelectElement.prototype;
  const valueDesc = Object.getOwnPropertyDescriptor(proto, 'value');
  const indexDesc = Object.getOwnPropertyDescriptor(proto, 'selectedIndex');
  let uid = 0;
  let openInstance = null;

  class AM2TSelect {
    constructor(select) {
      this.select = select;
      this.id = `am2t-select-${++uid}`;
      this.activeIndex = -1;
      this.typed = '';
      this.typedTimer = null;
      select.setAttribute('data-am2t-enhanced', '');

      this.build();
      this.patchValue();
      this.observe();
      this.render();
    }

    build() {
      const s = this.select;
      this.wrap = document.createElement('div');
      this.wrap.className = 'am2t-select';
      s.parentNode.insertBefore(this.wrap, s);
      this.wrap.appendChild(s);
      s.classList.add('am2t-select__native');
      s.tabIndex = -1;
      s.setAttribute('aria-hidden', 'true');

      this.button = document.createElement('button');
      this.button.type = 'button';
      this.button.setAttribute('aria-haspopup', 'listbox');
      this.button.setAttribute('aria-expanded', 'false');
      this.button.setAttribute('aria-controls', `${this.id}-list`);
      this.label = document.createElement('span');
      this.label.className = 'am2t-select__value';
      this.button.appendChild(this.label);
      this.wrap.appendChild(this.button);

      this.list = document.createElement('ul');
      this.list.id = `${this.id}-list`;
      this.list.className = 'am2t-select__list';
      this.list.setAttribute('role', 'listbox');
      this.list.tabIndex = -1;
      this.list.hidden = true;
      this.wrap.appendChild(this.list);

      // The <label for="…"> keeps working: clicking it focuses the button.
      if (s.id) {
        document.querySelectorAll(`label[for="${CSS.escape(s.id)}"]`).forEach((l) => {
          if (!l.id) l.id = `${this.id}-label`;
          this.button.setAttribute('aria-labelledby', `${l.id} ${this.id}-btn`);
          l.addEventListener('click', (e) => { e.preventDefault(); this.button.focus(); });
        });
      }
      this.button.id = `${this.id}-btn`;
      if (!this.button.hasAttribute('aria-labelledby') && s.getAttribute('aria-label')) {
        this.button.setAttribute('aria-label', s.getAttribute('aria-label'));
      }

      this.button.addEventListener('click', () => (this.isOpen() ? this.close() : this.open()));
      this.button.addEventListener('keydown', (e) => this.onButtonKey(e));
      this.list.addEventListener('keydown', (e) => this.onListKey(e));
      this.list.addEventListener('click', (e) => {
        const li = e.target.closest('[role="option"]');
        if (li && li.getAttribute('aria-disabled') !== 'true') this.choose(+li.dataset.index);
      });
      this.list.addEventListener('mousemove', (e) => {
        const li = e.target.closest('[role="option"]');
        if (li) this.setActive(+li.dataset.index, false);
      });
      s.addEventListener('change', () => this.render());
      s.form?.addEventListener('reset', () => setTimeout(() => this.render()));
    }

    // select.value = x / select.selectedIndex = n do not fire events;
    // wrap them on this instance so the visible label follows.
    patchValue() {
      const self = this;
      Object.defineProperty(this.select, 'value', {
        configurable: true,
        get() { return valueDesc.get.call(this); },
        set(v) { valueDesc.set.call(this, v); self.render(); }
      });
      Object.defineProperty(this.select, 'selectedIndex', {
        configurable: true,
        get() { return indexDesc.get.call(this); },
        set(v) { indexDesc.set.call(this, v); self.render(); }
      });
    }

    observe() {
      this.observer = new MutationObserver(() => this.render());
      this.observer.observe(this.select, {
        attributes: true,
        attributeFilter: ['class', 'hidden', 'disabled', 'required', 'aria-invalid'],
        childList: true,
        subtree: true,
        characterData: true
      });
    }

    render() {
      const s = this.select;
      // Trigger wears the select's own classes, so the existing look of
      // each context (contact form line, Competition Pack box…) is kept.
      const classes = Array.from(s.classList).filter((c) => c !== 'am2t-select__native');
      this.button.className = ['am2t-select__button', ...classes].join(' ');
      this.wrap.hidden = s.hidden;
      this.button.disabled = s.disabled;
      if (s.getAttribute('aria-invalid')) this.button.setAttribute('aria-invalid', s.getAttribute('aria-invalid'));
      else this.button.removeAttribute('aria-invalid');

      const sel = s.options[indexDesc.get.call(s)];
      this.label.textContent = sel ? sel.text : '';
      this.button.classList.toggle('is-placeholder', !sel || sel.value === '');

      if (this.isOpen()) this.renderOptions();
    }

    renderOptions() {
      const s = this.select;
      const current = indexDesc.get.call(s);
      this.list.textContent = '';
      Array.from(s.options).forEach((opt, i) => {
        if (opt.hidden) return;
        const li = document.createElement('li');
        li.id = `${this.id}-opt-${i}`;
        li.className = 'am2t-select__option';
        li.setAttribute('role', 'option');
        li.dataset.index = i;
        li.textContent = opt.text;
        li.setAttribute('aria-selected', i === current ? 'true' : 'false');
        if (opt.disabled) li.setAttribute('aria-disabled', 'true');
        this.list.appendChild(li);
      });
    }

    isOpen() { return !this.list.hidden; }

    open() {
      if (this.select.disabled) return;
      if (openInstance && openInstance !== this) openInstance.close(false);
      openInstance = this;
      this.renderOptions();
      this.list.hidden = false;
      this.wrap.classList.add('is-open');
      this.button.setAttribute('aria-expanded', 'true');
      this.position();
      this.setActive(Math.max(0, indexDesc.get.call(this.select)), true);
      this.list.focus({ preventScroll: true });
      this.outside = (e) => { if (!this.wrap.contains(e.target)) this.close(false); };
      document.addEventListener('pointerdown', this.outside, true);
      window.addEventListener('resize', this.onResize = () => this.close(false), { once: true });
    }

    close(focusButton = true) {
      if (!this.isOpen()) return;
      this.list.hidden = true;
      this.wrap.classList.remove('is-open', 'is-up');
      this.button.setAttribute('aria-expanded', 'false');
      this.list.removeAttribute('aria-activedescendant');
      document.removeEventListener('pointerdown', this.outside, true);
      if (openInstance === this) openInstance = null;
      if (focusButton) this.button.focus({ preventScroll: true });
    }

    // Open upwards when there is not enough room below.
    position() {
      const r = this.button.getBoundingClientRect();
      const h = Math.min(this.list.scrollHeight, 280);
      const below = window.innerHeight - r.bottom;
      this.wrap.classList.toggle('is-up', below < h + 16 && r.top > below);
    }

    enabledIndexes() {
      return Array.from(this.select.options)
        .map((o, i) => (!o.disabled && !o.hidden ? i : -1))
        .filter((i) => i >= 0);
    }

    setActive(index, scroll) {
      this.activeIndex = index;
      this.list.querySelectorAll('.is-active').forEach((el) => el.classList.remove('is-active'));
      const li = this.list.querySelector(`[data-index="${index}"]`);
      if (!li) return;
      li.classList.add('is-active');
      this.list.setAttribute('aria-activedescendant', li.id);
      if (scroll) li.scrollIntoView({ block: 'nearest' });
    }

    move(step) {
      const idx = this.enabledIndexes();
      if (!idx.length) return;
      let pos = idx.indexOf(this.activeIndex);
      pos = pos === -1 ? (step > 0 ? 0 : idx.length - 1) : Math.min(idx.length - 1, Math.max(0, pos + step));
      this.setActive(idx[pos], true);
    }

    choose(index) {
      const s = this.select;
      const changed = indexDesc.get.call(s) !== index;
      indexDesc.set.call(s, index);
      this.render();
      this.close();
      if (changed) {
        s.dispatchEvent(new Event('input', { bubbles: true }));
        s.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    typeAhead(key) {
      clearTimeout(this.typedTimer);
      this.typed += key.toLowerCase();
      this.typedTimer = setTimeout(() => { this.typed = ''; }, 600);
      const hit = this.enabledIndexes().find((i) => this.select.options[i].text.trim().toLowerCase().startsWith(this.typed));
      if (hit !== undefined) return hit;
      return -1;
    }

    onButtonKey(e) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
        e.preventDefault();
        this.open();
      } else if (e.key.length === 1 && /\S/.test(e.key)) {
        const hit = this.typeAhead(e.key);
        if (hit >= 0) this.choose(hit);
      }
    }

    onListKey(e) {
      switch (e.key) {
        case 'ArrowDown': e.preventDefault(); this.move(1); break;
        case 'ArrowUp': e.preventDefault(); this.move(-1); break;
        case 'Home': e.preventDefault(); this.setActive(this.enabledIndexes()[0], true); break;
        case 'End': e.preventDefault(); this.setActive(this.enabledIndexes().slice(-1)[0], true); break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          if (this.activeIndex >= 0 && !this.select.options[this.activeIndex]?.disabled) this.choose(this.activeIndex);
          break;
        case 'Escape': e.preventDefault(); this.close(); break;
        case 'Tab': this.close(false); break;
        default:
          if (e.key.length === 1 && /\S/.test(e.key)) {
            const hit = this.typeAhead(e.key);
            if (hit >= 0) this.setActive(hit, true);
          }
      }
    }
  }

  const enhanceAll = (root = document) => {
    root.querySelectorAll(SELECTOR).forEach((s) => new AM2TSelect(s));
  };

  window.AM2TSelect = { enhanceAll };

  const start = () => {
    enhanceAll();
    // Selects added later (cart drawer, section reloads in the editor…).
    new MutationObserver((muts) => {
      for (const m of muts) {
        m.addedNodes.forEach((n) => {
          if (n.nodeType !== 1) return;
          if (n.matches?.(SELECTOR)) new AM2TSelect(n);
          else if (n.querySelector?.(SELECTOR)) enhanceAll(n);
        });
      }
    }).observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
